<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useNoteTypeStore } from "@/stores/useNoteTypeStore";
import { useUserStore } from "@/stores/useUserStore";

import { toTitleCase } from "@/utils/stringUtils";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";

// Componentes modais
import AddNoteTypeForm from "./AddNoteTypeForm.vue";
import EditNoteTypeForm from "./EditNoteTypeForm.vue";
import DeleteNoteTypeForm from "./DeleteNoteTypeForm.vue";
import NoteTypeCardModal from "./NoteTypeCardModal.vue";
import BatchDeleteNoteTypeForm from "./BatchDeleteNoteTypeForm.vue";
import SearchNoteTypeInput from "./SearchNoteTypeInput.vue";
import NoteTypeHelpModal from "./NoteTypeHelpModal.vue";

// Paginação inferior
import PaginationButtons from "../Generics/PaginationButtons.vue";

// Store
const noteTypeStore = useNoteTypeStore();
const userStore = useUserStore();

// Estados de exibição dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showCardModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);

const selectedNoteTypeId = ref(null);

const showHelpModal = ref(false);

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);

// Lógica de Dropdowns e Seleção
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ====================================================================
// ✅ LÓGICA DO TOAST UNIFICADA
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout(duration) {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, duration);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(6000); // 6s para erro (padrão usado nos outros)
}
// ====================================================================

// ====================================================================
// ✅ REGRA SYS / VISUAL (Opção A): checkbox desabilitado + esmaecido + cursor not-allowed + badge SYS
// (mantendo lógica intacta)
// ====================================================================
function normalizeUserName(name) {
  return (name ?? "").toString().trim().toLowerCase();
}
function isLoggedSys() {
  return normalizeUserName(userStore.currentUser?.userName) === "sys";
}
function isCreatedBySys(noteType) {
  return normalizeUserName(noteType?.createdByUserName) === "sys";
}

// Regra: não-sys NÃO pode selecionar itens criados pelo sys.
// (Obs: canManage continua sendo "sys pode tudo; demais só o que eles criaram", intacto)
function isRowSelectable(noteType) {
  const logged = normalizeUserName(userStore.currentUser?.userName);
  if (!logged) return false; // enquanto não carregou, não libera

  if (logged === "sys") return true;

  const creator = normalizeUserName(noteType?.createdByUserName);
  if (!creator) return true; // mantém tolerância (se não vier, não bloqueia)

  return creator !== "sys";
}

function sysProtectedTooltip(noteType) {
  if (!noteType) return "";
  if (isLoggedSys()) return "";
  if (!isCreatedBySys(noteType)) return "";
  return "Protegido (criado pelo Sys)";
}
// ====================================================================

// 🔳 Fundo quadriculado (mesmo padrão usado no NoteSubtypeForm)
const transparentBackgroundStyle = computed(() => {
  const isDark = document.documentElement.classList.contains("dark");
  const color = isDark ? "#555" : "#ccc";

  return {
    backgroundImage: `
        linear-gradient(45deg, ${color} 12.5%, transparent 12.5%, transparent 87.5%, ${color} 87.5%),
        linear-gradient(45deg, ${color} 12.5%, transparent 12.5%, transparent 87.5%, ${color} 87.5%)
      `,
    backgroundSize: "10px 10px",
    backgroundPosition: "0 0, 5px 5px",
    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
  };
});

/**
 * Preview para PALAVRAS ÚNICAS (chip)
 */
function getSingleWordPreviewStyle(noteType) {
  const isTransparent =
    noteType.defaultBackground === "transparent" ||
    !noteType.defaultRequiredBackground;

  let style = {};

  if (isTransparent) {
    style = {
      ...transparentBackgroundStyle.value,
      color: noteType.defaultColor,
    };
  } else {
    style = {
      backgroundColor: noteType.defaultBackground,
      color: noteType.defaultColor,
    };
  }

  return {
    ...style,
    borderRadius: "999px",
    padding: "0.2rem 0.45rem",
    display: "inline-block",
    border: "1px solid var(--border-color)",
    fontWeight: "bold",
    fontSize: "0.8rem",
    lineHeight: "1.1",
  };
}

/**
 * Container externo para o preview de TRECHOS COM VÁRIAS PALAVRAS
 */
function getMultiWordContainerStyle() {
  return {
    ...transparentBackgroundStyle.value,
    borderRadius: "8px",
    padding: "0.3rem 0.6rem",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid var(--border-color)",
  };
}

/**
 * Texto interno para TRECHOS COM VÁRIAS PALAVRAS
 */
function getMultiWordPreviewStyle(noteType) {
  const underlineColor =
    noteType.defaultUnderline || noteType.defaultColor || "#000000";

  return {
    display: "inline-block",
    color: "inherit",
    backgroundColor: "transparent",
    borderBottom: noteType.defaultRequiredUnderline
      ? `3px solid ${underlineColor}`
      : "none",
    paddingBottom: "1px",
    lineHeight: "1.1",
    fontSize: "0.8rem",
    fontWeight: "normal",
  };
}

const isAllSelectedOnPage = computed({
  get: () => {
    const selectableOnPage = (noteTypeStore.noteTypes ?? []).filter((nt) =>
      isRowSelectable(nt)
    );
    if (selectableOnPage.length === 0) return false;

    return selectableOnPage.every((nt) =>
      noteTypeStore.selectedNoteTypeIds.includes(nt.noteTypeId)
    );
  },
  set: (value) => {
    const idsOnPage = (noteTypeStore.noteTypes ?? []).map(
      (nt) => nt.noteTypeId
    );

    const selectableIdsOnPage = (noteTypeStore.noteTypes ?? [])
      .filter((nt) => isRowSelectable(nt))
      .map((nt) => nt.noteTypeId);

    if (value) {
      selectableIdsOnPage.forEach((id) => {
        if (!noteTypeStore.selectedNoteTypeIds.includes(id)) {
          noteTypeStore.selectedNoteTypeIds.push(id);
        }
      });
    } else {
      // remove IDs da página (inclusive não-selecionáveis, caso tenham sido selecionados por estado antigo)
      noteTypeStore.selectedNoteTypeIds =
        noteTypeStore.selectedNoteTypeIds.filter(
          (id) => !idsOnPage.includes(id)
        );
    }
  },
});

function toggleRowDropdown(noteTypeId) {
  openDropdownId.value =
    openDropdownId.value === noteTypeId ? null : noteTypeId;
}

function handleEdit(index) {
  indexSelector(index);
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  handleOpenCard(index);
  openDropdownId.value = null;
}

function handleOpenCard(index) {
  const noteType = noteTypeStore.noteTypes[index];
  selectedNoteTypeId.value = noteType.noteTypeId;
  noteTypeStore.setIdNoteType(noteType.noteTypeId);
  showCardModal.value = true;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

/**
 * ✅ Ações em lote com validação e toast
 */
function handleBatchDelete() {
  isBatchDropdownOpen.value = false;

  if (noteTypeStore.selectedNoteTypeIds.length === 0) {
    showErrorToast(
      "Selecione pelo menos uma categoria de nota para realizar esta ação."
    );
    return;
  }

  showBatchDeleteModal.value = true;
}

// ✅ Protege seleção do sys
function toggleSelection(noteType) {
  if (!isRowSelectable(noteType)) return;
  noteTypeStore.toggleSelection(noteType.noteTypeId);
}

// Fetch de dados
async function fetchNoteTypesPaged() {
  try {
    await noteTypeStore.fetchNoteTypesPaged(
      currentPage.value,
      pageSize.value,
      noteTypeStore.searchTerm
    );

    // ✅ Sanitize: se não-sys, remove seleções de itens do sys que apareçam na página atual
    if (!isLoggedSys() && Array.isArray(noteTypeStore.noteTypes)) {
      const sysIdsOnPage = noteTypeStore.noteTypes
        .filter((nt) => isCreatedBySys(nt))
        .map((nt) => nt.noteTypeId);

      if (sysIdsOnPage.length) {
        noteTypeStore.selectedNoteTypeIds =
          noteTypeStore.selectedNoteTypeIds.filter(
            (id) => !sysIdsOnPage.includes(id)
          );
      }
    }

    if (noteTypeStore.shouldForcePageOne) {
      noteTypeStore.shouldForcePageOne = false;

      if (currentPage.value !== 1) {
        currentPage.value = 1; // watcher chama fetch
      } else {
        // já está na 1: refetch explícito
        await noteTypeStore.fetchNoteTypesPaged(
          1,
          pageSize.value,
          noteTypeStore.searchTerm
        );

        if (!isLoggedSys() && Array.isArray(noteTypeStore.noteTypes)) {
          const sysIdsOnPage = noteTypeStore.noteTypes
            .filter((nt) => isCreatedBySys(nt))
            .map((nt) => nt.noteTypeId);

          if (sysIdsOnPage.length) {
            noteTypeStore.selectedNoteTypeIds =
              noteTypeStore.selectedNoteTypeIds.filter(
                (id) => !sysIdsOnPage.includes(id)
              );
          }
        }
      }
    }
  } catch (error) {
    console.error("❌ Erro ao buscar tipos de nota paginados:", error.message);
    showErrorToast("Erro ao carregar categorias de nota.");
  }
}

watch(currentPage, fetchNoteTypesPaged);

// Lógica para fechar dropdowns ao clicar fora
function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  fetchNoteTypesPaged();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

// Seleciona o ID do tipo de nota
function indexSelector(index) {
  const noteType = noteTypeStore.noteTypes[index];
  noteTypeStore.setIdNoteType(noteType.noteTypeId);
}

async function handleNoteTypeAdded() {
  if (currentPage.value !== 1) currentPage.value = 1;
  await fetchNoteTypesPaged(); // refetch mesmo se já estava na 1
}

function canManage(noteType) {
  const creator = (noteType?.createdByUserName ?? "").trim().toLowerCase();
  const logged = (userStore.currentUser?.userName ?? "").trim().toLowerCase();

  if (!logged) return false; // usuário ainda não carregou

  // sys pode tudo
  if (logged === "sys") return true;

  // demais usuários: só o que eles mesmos criaram
  return creator === logged;
}
</script>
  
  <template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Categorias de Nota</div>
          <div class="text-sm mt-1 text-gray-700 ">
            Gerencie as categorias-mãe usadas para organizar as notas das
            atividades e seus destaques visuais.
            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <div class="w-full md:w-1/2">
          <SearchNoteTypeInput />
        </div>
        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <button @click="showAddModal = true" class="btn btn-primary">
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Adicionar Categoria
          </button>
          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-secondary w-full"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>
            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <div class="py-1">
                <a
                  href="#"
                  role="button"
                  @click.prevent="handleBatchDelete"
                  class="dropdown-item danger-item"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                />
              </th>
              <th style="width: 5%">Ações</th>
              <th style="width: 5%">Id</th>
              <th style="width: 20%">Categoria</th>
              <th style="width: 15%">Nota única palavra</th>
              <th style="width: 15%">Nota trecho</th>
              <th style="width: 15%">Data Criação</th>
              <th style="width: 15%">Data Atualização</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(noteType, index) in noteTypeStore.noteTypes"
              :key="noteType.noteTypeId"
              @click="handleOpenCard(index)"
              class="clickable-row"
            >
              <td
                @click.stop
                :class="{
 'checkbox-cell-disabled': !isRowSelectable(noteType),
                }"
              >
                <input
                  type="checkbox"
                  :value="noteType.noteTypeId"
                  :checked="
                    noteTypeStore.selectedNoteTypeIds.includes(
                      noteType.noteTypeId
                    )
                  "
                  :disabled="!isRowSelectable(noteType)"
                  :title="sysProtectedTooltip(noteType)"
                  :class="[
 'checkbox-style',
                    { 'checkbox-disabled': !isRowSelectable(noteType) },
                  ]"
                  @change="toggleSelection(noteType)"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <button
                  @click="toggleRowDropdown(noteType.noteTypeId)"
                  class="btn-icon btn-pastel-green"
                  title="Menu de Ações"
                >
                  <i class="fas fa-ellipsis-h fa-lg"></i>
                </button>
                <div
                  v-if="openDropdownId === noteType.noteTypeId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      :class="{ 'disabled-item': !canManage(noteType) }"
                      @click="canManage(noteType) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li @click="handleView(index)">
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>

                    <li
                      class="danger-item"
                      :class="{ 'disabled-item': !canManage(noteType) }"
                      @click="canManage(noteType) && handleDelete(index)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="noteType.noteTypeId">
                  {{ noteType.noteTypeId }}
                </span>
              </td>

              <td class="cell-text-left">
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class="truncate"
                    :title="toTitleCase(noteType.noteTypeName)"
                  >
                    {{ toTitleCase(noteType.noteTypeName) }}
                  </span>

                  <!-- ✅ Badge SYS (Opção A) -->
                  <span
                    v-if="isCreatedBySys(noteType)"
                    class="sys-badge ml-2"
                    title="Criado pelo Sys"
                  >
                    SYS
                  </span>
                </div>
              </td>

              <td class="cell-preview-container">
                <div
                  :style="getSingleWordPreviewStyle(noteType)"
                  :title="`Cor do texto: ${noteType.defaultColor} | Fundo: ${noteType.defaultBackground}`"
                >
                  Palavra
                </div>
              </td>

              <td class="cell-preview-container">
                <div :style="getMultiWordContainerStyle()">
                  <span
                    :style="getMultiWordPreviewStyle(noteType)"
                    :title="`Sublinhado: ${
                      noteType.defaultRequiredUnderline
                        ? noteType.defaultUnderline
                        : 'Não'
                    }`"
                  >
                    Várias palavras
                  </span>
                </div>
              </td>

              <td>
                <span :title="formatDateTimeForDisplay(noteType.createdAt)">
                  {{ formatDateTimeForDisplay(noteType.createdAt) }}
                </span>
              </td>
              <td>
                <span :title="formatDateTimeForDisplay(noteType.updatedAt)">
                  {{ formatDateTimeForDisplay(noteType.updatedAt) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <PaginationButtons
          class="p-4"
          :current-page="currentPage"
          :page-size="pageSize"
          :total-count="noteTypeStore.totalCount ?? 0"
          :total-pages="noteTypeStore.totalPages ?? 0"
          @change-page="(page) => (currentPage = page)"
        />
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AddNoteTypeForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @note-type-added="handleNoteTypeAdded"
    />
    <EditNoteTypeForm
      v-if="showEditModal"
      @close="showEditModal = false"
      @refresh="fetchNoteTypesPaged"
    />
    <NoteTypeCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :note-type-id="selectedNoteTypeId"
      @close="showCardModal = false"
    />

    <DeleteNoteTypeForm
      v-if="showDeleteModal"
      @close="showDeleteModal = false"
      @refresh="fetchNoteTypesPaged"
    />
    <BatchDeleteNoteTypeForm
      v-if="showBatchDeleteModal"
      @close="showBatchDeleteModal = false"
      @refresh="fetchNoteTypesPaged"
    />
    <NoteTypeHelpModal :show="showHelpModal" @close="showHelpModal = false" />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Atenção" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>
  
  <style scoped>
@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.btn-pastel-green {
  background-color: #9bf6ff;
  color: #1a4a4f;
}

.dropdown-menu {
  margin-top: 0.5rem;
}

.row-dropdown-menu {
  left: 50px;
  min-width: 170px;
}

/* 🔽 PREVIEW – COMPACTO */
.main-table .cell-preview-container {
  padding: 0.25rem 0.2rem;
  display: table-cell;
  vertical-align: middle;
}

.checkbox-cell-disabled * {
  cursor: not-allowed !important;
}

</style>
  